'use client';

import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/atoms/DropdownMenu/DropdownMenu';
import { Skeleton } from '@/atoms/Skeleton/Skeleton';
import { PINAKES_RELATED_LIMIT } from '@/config/pinakes';
import { usePinakesRelated } from '@/hooks/usePinakesRelated/usePinakesRelated';
import { getDisplayLink, getResourceLink, getResourceListNote } from './ResourceCard.utils';
import { ResourceMenuRow } from './ResourceMenuRow';

interface ResourceSimilarProps {
  /** The card's Pinakes ID (`card.id`), what `related=` is asked for. */
  id: string;
  /** The card's title, so each card's button has its own accessible name in a feed. */
  title: string;
}

interface SimilarListProps {
  id: string;
  /** The live region the list's loading, empty and error messages are announced in. */
  announcer: HTMLElement | null;
}

/**
 * "Similar": an outlined button whose menu lists what the people who tagged the resource also
 * tagged (`GET /v1/search?related=<id>`), up to `PINAKES_RELATED_LIMIT` rows of favicon, title and
 * type. Read only when the menu opens: the list mounts with the menu, so a feed of cards spends
 * none of Pinakes's per-IP budget on it.
 *
 * The messages are announced in one live region that stays mounted beside the button, outside the
 * menu: a menu may own only items, and a region that appears with its text is often not read.
 */
export function ResourceSimilar({ id, title }: ResourceSimilarProps) {
  const [announcer, setAnnouncer] = useState<HTMLElement | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="resource-similar-trigger">
            {/* The space keeps "Similar" and the hidden suffix apart in the accessible name. */}
            Similar <span className="sr-only">to {title}</span>
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          data-testid="resource-similar-menu"
          className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-70 overflow-y-auto"
        >
          <SimilarList id={id} announcer={announcer} />
        </DropdownMenuContent>
      </DropdownMenu>
      <span ref={setAnnouncer} role="status" data-testid="resource-similar-status" className="sr-only" />
    </>
  );
}

function SimilarList({ id, announcer }: SimilarListProps) {
  const labelId = useId();
  const { data, isLoading, isError } = usePinakesRelated(id);
  const isEmpty = !data || data.length === 0;
  const message = isLoading
    ? 'Loading similar links'
    : isError
      ? 'Similar links could not be loaded'
      : isEmpty
        ? 'Nothing similar yet'
        : null;

  return (
    <>
      {announcer && message && createPortal(message, announcer)}
      <DropdownMenuLabel id={labelId} className="text-xs font-medium text-muted-foreground">
        People who tagged this also tagged
      </DropdownMenuLabel>
      {isLoading ? (
        <Container aria-hidden="true" data-testid="resource-similar-loading" className="gap-2 px-2 py-1.5">
          {Array.from({ length: 3 }, (_, index) => (
            <Container key={index} className="flex-row items-center gap-2">
              <Skeleton className="size-4 shrink-0" />
              <Skeleton className="h-4 flex-1" />
            </Container>
          ))}
        </Container>
      ) : isError || isEmpty ? (
        // A disabled item, so the menu still has an item to read when it has no rows.
        <DropdownMenuItem
          disabled
          data-testid={isError ? 'resource-similar-error' : 'resource-similar-empty'}
          className="text-muted-foreground data-[disabled]:opacity-100"
        >
          {message}
        </DropdownMenuItem>
      ) : (
        <DropdownMenuGroup aria-labelledby={labelId}>
          {/* Calendar occurrences share their feed's `id`: the index keeps the keys unique. */}
          {data.slice(0, PINAKES_RELATED_LIMIT).map((card, index) => {
            const href = getResourceLink(card);
            return (
              <ResourceMenuRow
                key={`${index}-${card.id}`}
                uri={href ?? card.uri}
                href={href}
                name={card.name || getDisplayLink(card.uri)}
                note={getResourceListNote(card, href)}
                testId="resource-similar-row"
              />
            );
          })}
        </DropdownMenuGroup>
      )}
    </>
  );
}
